import { getReservationFinancialGroup } from '../_utils';
import type { ResponseData } from '@vrplatform/data';

type ReservationResponse = ResponseData<'get:/reservations/{id}'>;
type ReservationFinancialLines = ReservationResponse['financials']['lines'];
type ReservationFinancialLine = NonNullable<ReservationFinancialLines>[number];

type ReservationPostingDateSource = Pick<
  ReservationResponse,
  'lines' | 'adjustments'
>;

type PostingDatePreview = NonNullable<
  NonNullable<
    ReservationResponse['financials']['lines']
  >[number]['postingDatePreview']
>;

export const getLedgerReservationDetailCurrency = ({
  teamCurrency,
  reservationCurrency,
}: {
  reservationCurrency: string | null | undefined;
  teamCurrency: string | null | undefined;
}) => {
  return teamCurrency ?? reservationCurrency ?? undefined;
};

const getPostingAtFromPreview = (
  postingDatePreview: PostingDatePreview | null | undefined
) => {
  const postingDates = [
    ...new Set(
      (postingDatePreview?.entries ?? []).map((entry) => entry.postingDate)
    ),
  ];

  return postingDates.length === 1 ? postingDates[0] : null;
};

export const getReservationAdjustmentPostingAtByLineId = (
  reservation: ReservationPostingDateSource,
  lineId: string | null | undefined,
  postingDatePreview?: PostingDatePreview | null
) => {
  if (!lineId) return null;

  const linePostingAt = reservation.lines?.find((line) => line.id === lineId);
  if (linePostingAt?.generalLedgerPostingAt) {
    return linePostingAt.generalLedgerPostingAt;
  }

  const adjustmentPostingAt = reservation.adjustments?.find(
    (adjustment) => adjustment.id === lineId
  );

  return (
    adjustmentPostingAt?.generalLedgerPostingAt ??
    getPostingAtFromPreview(postingDatePreview)
  );
};

type ReservationFeeFinancialLine = {
  id: string;
  description: string;
  status: ReservationFinancialLine['status'];
  reservationLine?: ReservationFinancialLine['line'];
  recurringFee: ReservationFinancialLine['recurringFee'];
  account: ReservationFinancialLine['account'];
  postingAt?: string | null;
  postingDatePreview?: ReservationFinancialLine['postingDatePreview'];
  type: 'adjustment' | 'financial';
  centTotal: number;
  lock: ReservationFinancialLine['lock'];
};

export const mapReservationFeeRows = ({
  reservation,
  financialLines,
  createId,
}: {
  reservation: ReservationPostingDateSource;
  financialLines: ReservationFinancialLines | undefined;
  createId: () => string;
}): ReservationFeeFinancialLine[] =>
  financialLines?.flatMap<ReservationFeeFinancialLine>((line) => {
    const isFeeAndCommission =
      line.party === 'manager' &&
      getReservationFinancialGroup(line) === 'feeAndCommission';

    if (!isFeeAndCommission) return [];

    return [
      {
        id: createId(),
        centTotal: line.amount,
        type:
          line.type === 'reservation_fee_adjustment'
            ? 'adjustment'
            : 'financial',
        recurringFee: line.recurringFee,
        description: line.name,
        reservationLine: line.line,
        status: line.status,
        account: line.account,
        postingAt: getReservationAdjustmentPostingAtByLineId(
          reservation,
          line.line?.id,
          line.postingDatePreview
        ),
        postingDatePreview: line.postingDatePreview,
        lock: line.lock,
      },
    ];
  }) ?? [];
